import {ToolCallStatus, useFrontendTool} from "@copilotkit/react-core/v2";
import {diagramStore} from "@project/app-state";
import {MermaidDiagram} from "@project/components/visualization/mermaid.diagram";
import {ChartBarIcon, DatabaseIcon, NodesIcon} from "@project/icons-web";
import {routes} from "@project/router";
import {useRouter} from "@tanstack/react-router";
import {z} from "zod";

import {ToolCallSummary} from "../components/tool-call-summary";
import {ToolCallWrapper} from "../components/tool-call-wrapper";
import {focusAppOnMobile} from "../state/mobile-assistant-ui.store";

/**
 * Registry for Generative UI components.
 * Maps agent tool calls to rich HeroUI components rendered inline in the chat.
 */
export function GenerativeUIRegistry() {
  const router = useRouter();

  // ─── Render Mermaid Diagram ─────────────────────────────────────────────
  useFrontendTool(
    {
      name: "renderMermaidDiagram",
      description: "Render a mermaid diagram given its code",
      parameters: z.object({
        title: z.string(),
        code: z.string(),
      }),
      handler: async () => "Diagram rendered successfully.",
      render: ({args, status}) => {
        const {title, code} = args;

        // Pre-generate a deterministic ID for this session (using simple hash/timestamp) to avoid React re-render looping,
        // or just let the store handle overwrites. We will just use the title as ID for simplicity, or timestamp.
        const diagramId = `diag-${Math.abs(
          (code || "").split("").reduce((a, b) => ((a << 5) - a + b.charCodeAt(0)) | 0, 0),
        )}`;

        // Hydrate store on render so the new tab can access it
        setTimeout(() => {
          if (title && code) {
            diagramStore.setState((s) => ({
              ...s,
              diagrams: {
                ...s.diagrams,
                [diagramId]: {title, code},
              },
            }));
          }
        }, 0);

        return (
          <ToolCallWrapper
            icon={<NodesIcon className="size-4" />}
            name="Mermaid Diagram"
            title={title}
            status={
              status === "complete"
                ? ToolCallStatus.Complete
                : status === "executing"
                  ? ToolCallStatus.Executing
                  : ToolCallStatus.InProgress
            }
            navigationAction={{
              label: "View Full Diagram",
              onPress: () => {
                focusAppOnMobile();
                router.navigate({to: routes.diagram.diagram(), params: {diagramId}});
              },
            }}
            defaultExpanded
          >
            <ToolCallSummary isLoading={!title || !code} loadingText="Generating diagram...">
              {code ? <MermaidDiagram code={code} className="min-h-50" isChatMode /> : null}
            </ToolCallSummary>
          </ToolCallWrapper>
        );
      },
    },
    [router],
  );

  // ─── Financial Report ───────────────────────────────────────────────────
  useFrontendTool(
    {
      name: "generateFinancialReport",
      description: "Render a financial report summary",
      parameters: z.object({
        quarter: z.string(),
        year: z.number(),
        department: z.string(),
      }),
      handler: async () => "Financial report displayed.",
      render: ({args, status}) => {
        const {quarter, year, department} = args;

        return (
          <ToolCallWrapper
            icon={<ChartBarIcon className="size-4" />}
            name="Financial Report"
            title={`${department} | ${quarter} ${year}`}
            status={
              status === "complete"
                ? ToolCallStatus.Complete
                : status === "executing"
                  ? ToolCallStatus.Executing
                  : ToolCallStatus.InProgress
            }
            actions={[
              {label: "Download PDF", onPress: () => alert("Downloading PDF...")},
              {label: "Email to Team", onPress: () => alert("Email sent.")},
            ]}
            defaultExpanded
          >
            <ToolCallSummary
              isLoading={!quarter || !year || !department}
              loadingText="Generating report..."
            >
              <ToolCallSummary.Row
                label="Revenue"
                value={<span className="font-mono">$1,250,000</span>}
              />
              <ToolCallSummary.Row
                label="Expenses"
                value={<span className="font-mono">$850,000</span>}
              />
              <ToolCallSummary.Separator />
              <ToolCallSummary.Row
                label={<span className="text-success font-bold">Net Profit</span>}
                value={<span className="text-success font-mono font-bold">$400,000</span>}
              />
            </ToolCallSummary>
          </ToolCallWrapper>
        );
      },
    },
    [],
  );

  // ─── ERP Database Search (MCP Mock) ──────────────────────────────────────
  useFrontendTool(
    {
      name: "search_erp_database",
      description: "Render ERP database search results",
      parameters: z.object({
        query: z.string(),
      }),
      handler: async () => "Database searched.",
      render: ({args, status}) => {
        const {query} = args;

        return (
          <ToolCallWrapper
            icon={<DatabaseIcon className="size-4" />}
            name="Search ERP Database"
            title={`Query: "${query}"`}
            resultLabel="Found 3 results"
            status={
              status === "complete"
                ? ToolCallStatus.Complete
                : status === "executing"
                  ? ToolCallStatus.Executing
                  : ToolCallStatus.InProgress
            }
            defaultExpanded
          >
            <ToolCallSummary isLoading={!query} loadingText="Searching...">
              <ul className="list-inside list-disc space-y-1 text-sm">
                <li>INV-2026-001 (Paid)</li>
                <li>INV-2026-002 (Pending)</li>
                <li>PO-2026-045 (Approved)</li>
              </ul>
            </ToolCallSummary>
          </ToolCallWrapper>
        );
      },
    },
    [],
  );

  return null;
}
